import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  Circle,
  MoreHorizontal,
  User,
  Briefcase,
  RotateCcw,
  Trash2,
  Edit3,
  ArrowRightLeft,
  X,
  Bell,
  History,
} from 'lucide-react';
import { format, differenceInDays, isToday, isTomorrow } from 'date-fns';
import type { ExtendedTask } from '@/hooks/useTasks';
import type { TaskPriority, TaskStatus } from '@/types/tasks';

interface TaskDetailProps {
  task: ExtendedTask | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (task: ExtendedTask) => void;
  onDelete: (taskId: string) => void;
  onComplete: (taskId: string) => void;
  onReopen: (taskId: string) => void;
  onAssign?: (taskId: string, assigneeId: string, assigneeName: string) => void;
  assignees?: { value: string; label: string }[];
}

const priorityConfig: Record<TaskPriority, { label: string; color: string; bg: string }> = {
  low: {
    label: 'Low Priority',
    color: 'text-muted-foreground dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-800',
  },
  medium: {
    label: 'Medium Priority',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  high: {
    label: 'High Priority',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
  },
  urgent: {
    label: 'Urgent Priority',
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
  },
};

const statusConfig: Record<TaskStatus, { label: string; color: string; icon: React.ElementType }> = {
  todo: { icon: Circle, label: 'To Do', color: 'text-muted-foreground' },
  in_progress: { icon: ArrowRightLeft, label: 'In Progress', color: 'text-[#2B7FFF]' },
  completed: { icon: CheckCircle2, label: 'Completed', color: 'text-green-500' },
  overdue: { icon: AlertCircle, label: 'Overdue', color: 'text-red-500' },
  cancelled: { icon: X, label: 'Cancelled', color: 'text-slate-400' },
};

export function TaskDetail({
  task,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onComplete,
  onReopen,
  onAssign,
  assignees = [
    { value: 'user1', label: 'Alex Johnson' },
    { value: 'user2', label: 'Jane Smith' },
    { value: 'user3', label: 'Mike Brown' },
  ],
}: TaskDetailProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!task) return null;

  const priority = priorityConfig[task.priority];
  const status = statusConfig[task.status];
  const StatusIcon = status.icon;

  const formatDueDate = (dateString?: string) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    
    if (isToday(date)) return `Today at ${format(date, 'h:mm a')}`;
    if (isTomorrow(date)) return `Tomorrow at ${format(date, 'h:mm a')}`;
    
    const daysDiff = differenceInDays(date, new Date());
    if (daysDiff < 0) {
      return `${Math.abs(daysDiff)} day${Math.abs(daysDiff) > 1 ? 's' : ''} ago`;
    }
    
    return format(date, 'EEEE, MMMM d, yyyy h:mm a');
  };

  const getReminderText = (minutes?: number) => {
    if (!minutes || minutes === 0) return 'No reminder';
    if (minutes < 60) return `${minutes} minutes before`;
    if (minutes === 60) return '1 hour before';
    if (minutes === 1440) return '1 day before';
    return `${Math.round(minutes / 60)} hours before`;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg p-0">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-6">
            {/* Header */}
            <SheetHeader className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={cn(priority.bg, priority.color)}>
                    {priority.label}
                  </Badge>
                  {task.recurrence && (
                    <Badge variant="outline" className="gap-1">
                      <RotateCcw className="h-3 w-3" />
                      Recurring
                    </Badge>
                  )}
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(task)}>
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Task
                    </DropdownMenuItem>
                    
                    {task.status === 'completed' ? (
                      <DropdownMenuItem onClick={() => onReopen(task.id)}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reopen Task
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => onComplete(task.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark Complete
                      </DropdownMenuItem>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Task
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <SheetTitle className={cn(
                'text-xl leading-tight',
                task.status === 'completed' && 'line-through text-muted-foreground'
              )}>
                {task.title}
              </SheetTitle>
            </SheetHeader>

            {/* Status Banner */}
            <div className={cn(
              'flex items-center gap-3 p-4 rounded-xl',
              task.status === 'overdue' && 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800',
              task.status === 'completed' && 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800',
              task.status === 'todo' && 'bg-muted dark:bg-slate-800/50 border border-border dark:border-slate-700',
              task.status === 'in_progress' && 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800',
            )}>
              <div className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center',
                task.status === 'overdue' && 'bg-red-100 dark:bg-red-900',
                task.status === 'completed' && 'bg-green-100 dark:bg-green-900',
                task.status === 'todo' && 'bg-slate-100 dark:bg-slate-700',
                task.status === 'in_progress' && 'bg-blue-100 dark:bg-blue-900',
              )}>
                <StatusIcon className={cn('h-5 w-5', status.color)} />
              </div>
              <div className="flex-1">
                <p className={cn('font-medium', status.color)}>{status.label}</p>
                <p className="text-sm text-muted-foreground">
                  {task.status === 'completed' && task.completedAt
                    ? `Completed on ${format(new Date(task.completedAt), 'MMM d, yyyy')}`
                    : task.status === 'overdue'
                    ? 'This task is past its due date'
                    : 'This task is pending'}
                </p>
              </div>
              {task.status !== 'completed' && task.status !== 'cancelled' && (
                <Button
                  variant={task.status === 'overdue' ? 'destructive' : 'default'}
                  size="sm"
                  onClick={() => onComplete(task.id)}
                  className={task.status !== 'overdue' ? 'bg-[#2B7FFF] hover:bg-[#1A5FCC]' : undefined}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete
                </Button>
              )}
            </div>

            {/* Details */}
            <div className="space-y-4">
              {/* Due Date */}
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className={cn(
                    'font-medium',
                    task.status === 'overdue' && 'text-red-600 dark:text-red-400'
                  )}>
                    {formatDueDate(task.dueDate)}
                  </p>
                </div>
              </div>

              {/* Assignee */}
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Assigned To</p>
                  <div className="flex items-center gap-2">
                    {task.assigneeName ? (
                      <>
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs bg-[#2B7FFF]/10 text-[#2B7FFF]">
                            {task.assigneeName.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{task.assigneeName}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground italic">Unassigned</span>
                    )}
                  </div>
                </div>
                {onAssign && task.status !== 'completed' && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                        Reassign
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {assignees.map((assignee) => (
                        <DropdownMenuItem
                          key={assignee.value}
                          onClick={() => onAssign(task.id, assignee.value, assignee.label)}
                          disabled={task.assignedTo === assignee.value}
                        >
                          {assignee.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Reminder */}
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Reminder</p>
                  <p className="font-medium">{getReminderText(task.reminderMinutes)}</p>
                </div>
              </div>

              {/* Linked Contact */}
              {task.linkedContactName && (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Linked Contact</p>
                    <p className="font-medium">{task.linkedContactName}</p>
                  </div>
                </div>
              )}

              {/* Linked Deal */}
              {task.linkedDealName && (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Linked Deal</p>
                    <p className="font-medium">{task.linkedDealName}</p>
                  </div>
                </div>
              )}

              {/* Recurrence Info */}
              {task.recurrence && (
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <RotateCcw className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Recurrence</p>
                    <p className="font-medium capitalize">
                      Every {task.recurrence.interval} {task.recurrence.type}
                      {task.recurrence.interval > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Description */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Description</h4>
              {task.description ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{task.description}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No description provided</p>
              )}
            </div>

            <Separator />

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <History className="h-3.5 w-3.5" />
                Created {format(new Date(task.createdAt), 'MMM d, yyyy')}
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-4"
            >
              <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
                <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Delete Task?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    onDelete(task.id);
                    setShowDeleteConfirm(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default TaskDetail;

